/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Controller, UseFormResetField, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import useCheckoutPageProps from "@hooks/useCheckoutPageProps";

import { Text } from "@/components/Text";

import {
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    Input
} from "@chakra-ui/react";

import { ClassesEnum } from "@ts/enums/ClassesEnum";

import { getTrackingId } from "@utils/utils";

import {
    CountryOption,
    CountryValueContainer,
    Select as SelectComponent
} from "@components/Select";

export interface BillingAddressInfoInterface {
    countryInput: string;
    addressLine1Input: string;
    addressLine2Input?: string | null;
    cityInput: string;
    inputState: string;
    postalCodeInput?: string | null;
    countryCodeInput: string;
    phoneNumberInput: string;
}

interface PropTypes {
    errors: any;
    control: any;
    touchedFields: any;
    onResetField: UseFormResetField<BillingAddressInfoInterface>;
}

const BillingAddressContainer = ({
    errors,
    control,
    touchedFields,
    onResetField
}: PropTypes) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const { allCountries = [] } = useCheckoutPageProps();
    const { register, setValue } = useFormContext();
    const searchParamCountry = searchParams.get("selectedCountry");
    const [selectedCountry, setSelectedCountry] = useState<string | null>(
        searchParamCountry
    );
    const [initialized, setInitialized] = useState<boolean>(false);

    const availableCountries = allCountries.filter(country => country.label.toLowerCase() !== "international");

    const countriesOptions = availableCountries.map(c => ({
        value: c.value,
        label: c.label
    }));
    const countriesCodeOptions = availableCountries.map(c => ({
        value: c.value,
        label: c.calling_code
    }));
    const stateOptions = useMemo(() => {
        const [country] = availableCountries.filter(c => c.value === selectedCountry);

        const options =
            country?.states.map(state => ({
                value: state.value,
                label: state.label
            })) ?? [];

        return options || [];
    }, [selectedCountry]);

    const handleInputChange = (
        e: ChangeEvent<HTMLInputElement>,
        setValue: (
            name: keyof BillingAddressInfoInterface,
            value: any,
            options?: any
        ) => void
    ) => {
        const { name, value } = e.target;

        setValue(name as keyof BillingAddressInfoInterface, value, {
            shouldValidate: true
        });
    };

    const getStatesByCountry = (countryId: string) => {
        const country = availableCountries.find(c => c.value === countryId);
        return country ? country.states : [];
    };

    useEffect(() => {
        if (selectedCountry !== "international") {
            const [countryCode] = countriesCodeOptions.filter(
                country => country.value === selectedCountry
            );

            setValue("countryInput", selectedCountry);
            setValue("countryCodeInput", countryCode?.label);
        }
        setInitialized(true);
    }, [selectedCountry]);

    return (
        <>
            <Text fontSize={16}>
                {" "}
                {t(
                    "checkoutPage.checkout.billingAddress",
                    "Billing Address"
                )}{" "}
            </Text>
            {initialized && (
                <FormControl
                    fontSize={15}
                    className={ClassesEnum.FormField}
                    data-tracking-id={getTrackingId(
                        [
                            ClassesEnum.BillingAddressContainer,
                            ClassesEnum.FormField
                        ],
                        "inputCountry"
                    )}
                    isInvalid={
                        !!errors.countryInput && touchedFields["countryInput"]
                    }
                >
                    <FormLabel htmlFor="country" fontSize={14}>
                        {t("checkoutPage.country", "Country")}
                    </FormLabel>
                    <Controller
                        control={control}
                        {...register("countryInput")}
                        render={({ field: { onChange, value } }) => {
                            return (
                                <SelectComponent
                                    {...register("countryInput")}
                                    option={CountryOption}
                                    valueContainer={CountryValueContainer}
                                    value={
                                        countriesOptions.find(
                                            o => o?.value === value
                                        ) ?? null
                                    }
                                    styles={{
                                        control: {
                                            borderRadius: 6
                                        }
                                    }}
                                    onChange={e => {
                                        onChange(e?.value);
                                        onResetField("inputState", {
                                            defaultValue: ""
                                        });
                                        setSelectedCountry(e?.value ?? "AU");
                                    }}
                                    options={countriesOptions}
                                    placeholder={
                                        <Trans>Select your location</Trans>
                                    }
                                    isInvalid={!!errors.countryInput?.message}
                                />
                            );
                        }}
                    />
                    <FormErrorMessage>
                        {t(errors.countryInput?.message as string)}
                    </FormErrorMessage>
                </FormControl>
            )}

            <FormControl
                fontSize={15}
                className={ClassesEnum.FormField}
                data-tracking-id={getTrackingId(
                    [
                        ClassesEnum.BillingAddressContainer,
                        ClassesEnum.FormField
                    ],
                    "inputAddressLine1"
                )}
                isInvalid={
                    !!errors.addressLine1Input &&
                    touchedFields["addressLine1Input"]
                }
            >
                <FormLabel htmlFor="addressLine1" fontSize={14}>
                    {t("checkoutPage.addressLine1", "Address line 1")}
                </FormLabel>
                <Input
                    {...register("addressLine1Input")}
                    className={ClassesEnum.Input}
                    data-tracking-id={getTrackingId(
                        [
                            ClassesEnum.BillingAddressContainer,
                            ClassesEnum.FormField,
                            ClassesEnum.Input
                        ],
                        "inputAddressLine1"
                    )}
                    id="addressLine1"
                    maxLength={200}
                    onChange={e => handleInputChange(e, setValue)}
                />
                <FormErrorMessage>
                    {t(errors.addressLine1Input?.message as string)}
                </FormErrorMessage>
            </FormControl>

            <FormControl
                fontSize={15}
                className={ClassesEnum.FormField}
                data-tracking-id={getTrackingId(
                    [
                        ClassesEnum.BillingAddressContainer,
                        ClassesEnum.FormField
                    ],
                    "inputAddressLine2"
                )}
                isInvalid={
                    !!errors.addressLine2Input &&
                    touchedFields["addressLine2Input"]
                }
            >
                <FormLabel htmlFor="addressLine2" fontSize={14}>
                    {t(
                        "checkoutPage.addressLine2",
                        "Address line 2 (Optional)"
                    )}
                </FormLabel>
                <Input
                    {...register("addressLine2Input")}
                    className={ClassesEnum.Input}
                    data-tracking-id={getTrackingId(
                        [
                            ClassesEnum.BillingAddressContainer,
                            ClassesEnum.FormField,
                            ClassesEnum.Input
                        ],
                        "inputAddressLine2"
                    )}
                    id="addressLine2"
                    maxLength={200}
                    onChange={e => handleInputChange(e, setValue)}
                />
                <FormErrorMessage>
                    {t(errors.addressLine2Input?.message as string)}
                </FormErrorMessage>
            </FormControl>

            <FormControl
                fontSize={15}
                className={ClassesEnum.FormField}
                data-tracking-id={getTrackingId(
                    [
                        ClassesEnum.BillingAddressContainer,
                        ClassesEnum.FormField
                    ],
                    "inputCity"
                )}
                isInvalid={!!errors.cityInput && touchedFields["cityInput"]}
            >
                <FormLabel htmlFor="city" fontSize={14}>
                    {t("checkoutPage.suburb", "Suburb or City")}
                </FormLabel>
                <Input
                    {...register("cityInput")}
                    className={ClassesEnum.Input}
                    data-tracking-id={getTrackingId(
                        [
                            ClassesEnum.BillingAddressContainer,
                            ClassesEnum.FormField,
                            ClassesEnum.Input
                        ],
                        "inputCity"
                    )}
                    id="city"
                    maxLength={50}
                    onChange={e => handleInputChange(e, setValue)}
                />
                <FormErrorMessage>
                    {t(errors.cityInput?.message as string)}
                </FormErrorMessage>
            </FormControl>

            <Grid templateColumns="8fr 4fr" gap={4}>
                {initialized && (
                    <FormControl
                        fontSize={15}
                        className={ClassesEnum.FormField}
                        data-tracking-id={getTrackingId(
                            [
                                ClassesEnum.BillingAddressContainer,
                                ClassesEnum.FormField
                            ],
                            "inputState"
                        )}
                        isInvalid={
                            !!errors.inputState && touchedFields["inputState"]
                        }
                    >
                        <FormLabel htmlFor="state" fontSize={14}>
                            {t("checkoutPage.state", "State")}
                        </FormLabel>
                        <Controller
                            control={control}
                            rules={{ required: true }}
                            {...register("inputState")}
                            render={({ field: { onChange, value } }) => {
                                return (
                                    <SelectComponent
                                        value={
                                            stateOptions.find(
                                                o => o.value === value
                                            ) || null
                                        }
                                        onChange={v => {
                                            onChange(v?.value);
                                        }}
                                        options={stateOptions}
                                        placeholder={
                                            <Trans>Select your location</Trans>
                                        }
                                        styles={{
                                            control: {
                                                borderRadius: 6
                                            }
                                        }}
                                        isInvalid={!!errors.inputState?.message}
                                        isDisabled={
                                            getStatesByCountry(
                                                selectedCountry ?? "AU"
                                            ).length < 1
                                        }
                                    />
                                );
                            }}
                        />
                        <FormErrorMessage>
                            {t(errors.inputState?.message as string)}
                        </FormErrorMessage>
                    </FormControl>
                )}

                <FormControl
                    fontSize={15}
                    className={ClassesEnum.FormField}
                    data-tracking-id={getTrackingId(
                        [
                            ClassesEnum.BillingAddressContainer,
                            ClassesEnum.FormField
                        ],
                        "inputPostalCode"
                    )}
                    isInvalid={
                        !!errors.postalCodeInput &&
                        touchedFields["postalCodeInput"]
                    }
                >
                    <FormLabel htmlFor="postalCode" fontSize={14}>
                        {t("checkoutPage.postalCode", "Postal Code")}
                    </FormLabel>
                    <Input
                        {...register("postalCodeInput")}
                        className={ClassesEnum.Input}
                        data-tracking-id={getTrackingId(
                            [
                                ClassesEnum.BillingAddressContainer,
                                ClassesEnum.FormField,
                                ClassesEnum.Input
                            ],
                            "inputPostalCode"
                        )}
                        id="postalCode"
                        maxLength={15}
                        onChange={e => handleInputChange(e, setValue)}
                    />
                    <FormErrorMessage>
                        {t(errors.postalCodeInput?.message as string)}
                    </FormErrorMessage>
                </FormControl>
            </Grid>

            <FormControl
                fontSize={15}
                className={ClassesEnum.FormField}
                data-tracking-id={getTrackingId(
                    [
                        ClassesEnum.BillingAddressContainer,
                        ClassesEnum.FormField
                    ],
                    "inputPhoneNumber"
                )}
                isInvalid={
                    !!errors.phoneNumberInput &&
                    touchedFields["phoneNumberInput"]
                }
            >
                <FormLabel htmlFor="phoneNumber" fontSize={14}>
                    {t("checkoutPage.phoneNumber", "Phone number")}
                </FormLabel>
                <Grid templateColumns="4fr 8fr" gap={0} alignItems="center">
                    {initialized && (
                        <Controller
                            control={control}
                            {...register("countryCodeInput")}
                            render={({ field: { onChange, value } }) => {
                                const selectedValue =
                                    countriesCodeOptions.find(
                                        o => o?.label === value
                                    ) ?? null;

                                return (
                                    <SelectComponent
                                        option={CountryOption}
                                        valueContainer={CountryValueContainer}
                                        value={selectedValue}
                                        onChange={v => {
                                            onChange(v?.label);
                                        }}
                                        options={countriesCodeOptions}
                                        placeholder={null}
                                        styles={{
                                            control: {
                                                borderRadius: "6px 0px 0px 6px"
                                            }
                                        }}
                                        isInvalid={
                                            !!errors.phoneNumberInput?.message
                                        }
                                    />
                                );
                            }}
                        />
                    )}
                    <Input
                        className={ClassesEnum.Input}
                        data-tracking-id={getTrackingId(
                            [
                                ClassesEnum.BillingAddressContainer,
                                ClassesEnum.FormField,
                                ClassesEnum.Input
                            ],
                            "inputPhoneNumber"
                        )}
                        {...register("phoneNumberInput")}
                        id="phoneNumber"
                        style={{ borderRadius: "0 6px 6px 0" }}
                        onChange={e => handleInputChange(e, setValue)}
                    />
                </Grid>
                <FormErrorMessage>
                    {t(errors.phoneNumberInput?.message as string)}
                </FormErrorMessage>
            </FormControl>
        </>
    );
};

export default BillingAddressContainer;
